import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import { getAppPortalRoot } from "../utils/appPortal";
import {
  LIBRARY_READING_DEFAULT_MEDALLION_ID,
  LIBRARY_READING_MEDALLIONS,
} from "../constants/libraryReadingMedallions";
import {
  LIBRARY_READING_TIMER_DEFAULT_MINUTES,
  LIBRARY_READING_TIMER_DEFAULT_MINUTE_COLOR,
  LIBRARY_READING_TIMER_DEFAULT_SECOND_COLOR,
  LIBRARY_READING_TIMER_MAX_MINUTES,
  LIBRARY_READING_TIMER_MIN_MINUTES,
  clampLibraryReadingTimerMinutes,
  readLibraryReadingTimer,
  readLibraryReadingPreferencesView,
  writeLibraryReadingPreferencesView,
  writeLibraryReadingTimer,
} from "../utils/libraryReadingTimer";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";
import "./LibraryReadingPreferencesModal.css";

function RadioChip({ name, label, checked, onChange }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function LibraryReadingPreferencesModal({ open, onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [minutesText, setMinutesText] = useState(String(LIBRARY_READING_TIMER_DEFAULT_MINUTES));
  const [minuteColor, setMinuteColor] = useState(LIBRARY_READING_TIMER_DEFAULT_MINUTE_COLOR);
  const [secondColor, setSecondColor] = useState(LIBRARY_READING_TIMER_DEFAULT_SECOND_COLOR);
  const [discrete, setDiscrete] = useState(false);
  const [medallionId, setMedallionId] = useState(LIBRARY_READING_DEFAULT_MEDALLION_ID);
  const [panel, setPanel] = useState(() => readLibraryReadingPreferencesView().panel);
  const [wasOpen, setWasOpen] = useState(open);
  const viewRef = useRef(readLibraryReadingPreferencesView());
  const draftRef = useRef({});
  const dismissRef = useRef(() => {});
  const medallionListRef = useRef(null);
  const {
    trackRef: medallionTrackRef,
    scrollbar: medallionScrollbar,
    updateScrollbar: updateMedallionScrollbar,
    handleThumbPointerDown: handleMedallionThumbPointerDown,
    handleTrackPointerDown: handleMedallionTrackPointerDown,
  } = useCustomScrollbar(medallionListRef, { minThumbHeight: 56 });

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const stored = readLibraryReadingTimer();
      setEnabled(stored.enabled);
      setMinutesText(String(stored.minutes));
      setMinuteColor(stored.minuteColor);
      setSecondColor(stored.secondColor);
      setDiscrete(Boolean(stored.discrete));
      setMedallionId(stored.medallionId);
      const view = readLibraryReadingPreferencesView();
      viewRef.current = view;
      setPanel(view.panel);
    }
  }

  draftRef.current = {
    enabled,
    minutesText,
    minuteColor,
    secondColor,
    discrete,
    medallionId,
    onClose,
  };

  const saveDraft = (overrides = {}, commitMinutes = false) => {
    const draft = { ...draftRef.current, ...overrides };
    const previous = readLibraryReadingTimer();
    writeLibraryReadingTimer({
      enabled: draft.enabled,
      minutes: commitMinutes
        ? clampLibraryReadingTimerMinutes(draft.minutesText)
        : previous.minutes,
      minuteColor: draft.minuteColor,
      secondColor: draft.secondColor,
      discrete: draft.discrete,
      medallionId: draft.medallionId,
    });
  };

  dismissRef.current = () => {
    saveDraft({}, true);
    draftRef.current.onClose?.();
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") dismissRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => {
      const list = medallionListRef.current;
      if (list) list.scrollTop = viewRef.current.scrollTop;
      updateMedallionScrollbar();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, panel, updateMedallionScrollbar]);

  if (!open) return null;

  const handleMinutesChange = (event) => {
    const raw = event.target.value.trim();
    if (raw === "") {
      setMinutesText("");
      return;
    }
    if (!/^\d+$/.test(raw)) return;
    const value = Number(raw);
    if (value > LIBRARY_READING_TIMER_MAX_MINUTES) {
      setMinutesText(String(LIBRARY_READING_TIMER_MAX_MINUTES));
      return;
    }
    if (value < LIBRARY_READING_TIMER_MIN_MINUTES) {
      if (raw.length === 1 && value >= 1) {
        setMinutesText(raw);
        return;
      }
      setMinutesText(String(LIBRARY_READING_TIMER_MIN_MINUTES));
      return;
    }
    setMinutesText(String(value));
  };

  const handleMinutesKeyDown = (event) => {
    if (event.key === "e" || event.key === "E" || event.key === "+" || event.key === "-" || event.key === ".") {
      event.preventDefault();
      return;
    }
    const value = Number(minutesText);
    if (!Number.isFinite(value)) return;
    if (event.key === "ArrowUp" && value >= LIBRARY_READING_TIMER_MAX_MINUTES) {
      event.preventDefault();
    }
    if (event.key === "ArrowDown" && value <= LIBRARY_READING_TIMER_MIN_MINUTES) {
      event.preventDefault();
    }
  };

  const handleMinutesBlur = () => {
    const next = String(clampLibraryReadingTimerMinutes(minutesText));
    setMinutesText(next);
    saveDraft({ minutesText: next }, true);
  };

  const handleApply = () => {
    dismissRef.current();
  };

  const showPanel = (next) => {
    setPanel(next);
    viewRef.current = writeLibraryReadingPreferencesView({
      panel: next,
      scrollTop: medallionListRef.current?.scrollTop ?? viewRef.current.scrollTop,
    });
  };

  const handleMedallionScroll = () => {
    updateMedallionScrollbar();
    const scrollTop = medallionListRef.current?.scrollTop || 0;
    viewRef.current = writeLibraryReadingPreferencesView({ scrollTop });
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismissRef.current();
      }}
    >
      <div
        className="delete-word-modal register-settings-modal library-reading-preferences"
        role="dialog"
        aria-modal="true"
        aria-labelledby="library-reading-preferences-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={() => dismissRef.current()}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h2
          id="library-reading-preferences-title"
          className="delete-word-modal__title delete-word-modal__title--gothic"
        >
          {panel === "medallions" ? "Médaillons de lecture" : "Minuteur de lecture"}
        </h2>

        <div className="register-settings-modal__body">
          <section
            className={`register-settings-modal__section library-reading-preferences__sizer${
              panel === "medallions" ? "" : " is-hidden"
            }`}
            aria-hidden={panel !== "medallions"}
          >
              <button
                type="button"
                className="library-reading-preferences__medallion-link"
                onClick={() => showPanel("settings")}
              >
                Retour
              </button>
              <div className="library-reading-preferences__medallion-frame">
                <div className="library-reading-preferences__medallion-fade">
                <ul
                  ref={medallionListRef}
                  className="library-reading-preferences__medallions"
                  onScroll={handleMedallionScroll}
                >
                  {LIBRARY_READING_MEDALLIONS.map((medallion) => {
                    const selected = medallion.id === medallionId;
                    return (
                      <li key={medallion.id} className="library-reading-preferences__medallion">
                        <span className="library-reading-preferences__medallion-art">
                          <img src={medallion.src} alt="" draggable={false} />
                        </span>
                        <span className="library-reading-preferences__medallion-name">
                          {medallion.label}
                        </span>
                        <button
                          type="button"
                          className={`library-reading-preferences__pick${
                            selected ? " is-selected" : ""
                          }`}
                          aria-pressed={selected}
                          aria-label={`Choisir ${medallion.label}`}
                          onClick={() => {
                            setMedallionId(medallion.id);
                            saveDraft({ medallionId: medallion.id });
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
                </div>
                <div
                  ref={medallionTrackRef}
                  className={`library-reading-preferences__scrollbar${
                    medallionScrollbar.visible ? "" : " is-hidden"
                  }`}
                  aria-hidden="true"
                  onPointerDown={handleMedallionTrackPointerDown}
                >
                  {medallionScrollbar.visible ? (
                    <div
                      className="library-reading-preferences__scrollbar-thumb"
                      style={{
                        height: `${medallionScrollbar.height}px`,
                        transform: `translateY(${medallionScrollbar.top}px)`,
                      }}
                      onPointerDown={handleMedallionThumbPointerDown}
                    />
                  ) : null}
                </div>
              </div>
          </section>
          {panel === "settings" && (
          <section className="register-settings-modal__section library-reading-preferences__settings">
            <h3 className="register-settings-modal__section-title">Minuteur de lecture</h3>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Minuteur de lecture"
            >
              <RadioChip
                name="library-reading-timer"
                label="Oui"
                checked={enabled}
                onChange={() => {
                  setEnabled(true);
                  saveDraft({ enabled: true });
                }}
              />
              <RadioChip
                name="library-reading-timer"
                label="Non"
                checked={!enabled}
                onChange={() => {
                  setEnabled(false);
                  saveDraft({ enabled: false });
                }}
              />
            </div>
            {enabled ? (
              <div className="library-reading-preferences__discrete">
                <h3 className="register-settings-modal__section-title">Médaillon de lecture</h3>
                <div
                  className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
                  role="radiogroup"
                  aria-label="Médaillon de lecture"
                >
                  <RadioChip
                    name="library-reading-timer-discrete"
                    label="Oui"
                    checked={discrete}
                    onChange={() => {
                      setDiscrete(true);
                      saveDraft({ discrete: true });
                    }}
                  />
                  <RadioChip
                    name="library-reading-timer-discrete"
                    label="Non"
                    checked={!discrete}
                    onChange={() => {
                      setDiscrete(false);
                      saveDraft({ discrete: false });
                    }}
                  />
                </div>
                <button
                  type="button"
                  className={`library-reading-preferences__medallion-link${
                    discrete ? "" : " is-reserved"
                  }`}
                  tabIndex={discrete ? 0 : -1}
                  aria-hidden={discrete ? undefined : true}
                  onClick={() => {
                    if (!discrete) return;
                    showPanel("medallions");
                  }}
                >
                  choisir un médaillon de lecture
                </button>
              </div>
            ) : null}
            {enabled && (
              <div className="library-reading-preferences__details">
                <label className="library-reading-preferences__duration">
                  <span>Durée (minutes)</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={LIBRARY_READING_TIMER_MIN_MINUTES}
                    max={LIBRARY_READING_TIMER_MAX_MINUTES}
                    step={1}
                    value={minutesText}
                    aria-label="Durée du minuteur en minutes"
                    onChange={handleMinutesChange}
                    onKeyDown={handleMinutesKeyDown}
                    onBlur={handleMinutesBlur}
                  />
                  <span className="library-reading-preferences__range">
                    De {LIBRARY_READING_TIMER_MIN_MINUTES} à {LIBRARY_READING_TIMER_MAX_MINUTES}{" "}
                    minutes
                  </span>
                </label>
                <div className="library-reading-preferences__colors">
                  <label className="library-reading-preferences__color">
                    <span>Couleur des minutes</span>
                    <span
                      className="library-reading-preferences__swatch"
                      style={{ backgroundColor: minuteColor }}
                    >
                      <input
                        type="color"
                        value={minuteColor}
                        aria-label="Couleur des minutes"
                        onChange={(event) => {
                          const value = event.target.value;
                          setMinuteColor(value);
                          saveDraft({ minuteColor: value });
                        }}
                      />
                    </span>
                  </label>
                  <label className="library-reading-preferences__color">
                    <span>Couleur des secondes</span>
                    <span
                      className="library-reading-preferences__swatch"
                      style={{ backgroundColor: secondColor }}
                    >
                      <input
                        type="color"
                        value={secondColor}
                        aria-label="Couleur des secondes"
                        onChange={(event) => {
                          const value = event.target.value;
                          setSecondColor(value);
                          saveDraft({ secondColor: value });
                        }}
                      />
                    </span>
                  </label>
                </div>
              </div>
            )}
          </section>
          )}
        </div>

        <div className="delete-word-modal__actions register-settings-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={() => dismissRef.current()}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
